import React, { useState, useEffect, useRef } from 'react';
import NotificationDropdown from './NotificationDropdown';
import { useNotifications } from '../hooks/useNotifications';
import socketService from '../services/socketService';

const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState(
    'Notification' in window ? Notification.permission : 'unsupported'
  );
  const dropdownRef = useRef(null);
  const bellRef = useRef(null);

  // Use the notifications hook with real-time updates
  const {
    unreadCount,
    loading: isLoading,
    error,
    connected,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead
  } = useNotifications({
    enableRealTime: true,
    enableBrowserNotifications: true
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        bellRef.current &&
        !bellRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleBellClick = () => {
    setIsOpen(!isOpen);
  };

  const handleNotificationRead = async (notificationId) => {
    try {
      await markAsRead(notificationId);
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleAllRead = async () => {
    try {
      await markAllAsRead();
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  const handleRequestPermission = async () => {
    const granted = await socketService.requestNotificationPermission();
    if (granted) {
      setNotificationPermission('granted');
    } else {
      setNotificationPermission('denied');
    }
  };

  // Update permission status when it changes
  useEffect(() => {
    if (!('Notification' in window)) return undefined;

    const checkPermission = () => {
      setNotificationPermission(Notification.permission);
    };

    // Check initially
    checkPermission();

    // Listen for permission changes (Safari doesn't support this, but Chrome does)
    const interval = setInterval(checkPermission, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative mr-[5px]">
      {/* Notification Bell */}
      <button
        ref={bellRef}
        onClick={handleBellClick}
        className={`relative p-2 rounded-full transition-colors duration-200 ${
          isOpen 
            ? 'bg-blue-100 text-blue-600' 
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
        }`}
        aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
        disabled={isLoading}
      >
        {/* Bell Icon */}
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>

        {/* Unread Count Badge */}
        {unreadCount > 0 && (
          <span className="absolute top-0.5 right-0.5 inline-flex items-center justify-center px-1 py-0.5 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full min-w-[14px] h-[14px]">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}


        {/* Loading Indicator */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
      </button>

      {/* Error Tooltip */}
      {error && (
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 bg-red-100 border border-red-300 text-red-700 px-2 py-1 rounded text-xs whitespace-nowrap z-50">
          {error}
        </div>
      )}

      {/* Notification Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute top-full right-0 mt-2 z-50"
        >
          {/* Browser Notification Permission Prompt */}
          {notificationPermission === 'default' && (
            <div className="mb-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-80">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 p-1.5 bg-blue-50 rounded-lg">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">
                    Enable Browser Notifications
                  </h4>
                  <p className="text-xs text-gray-600 mb-3 leading-relaxed">
                    Get instant alerts for new proposals, messages, and updates even when this tab isn't active.
                  </p>
                  <button
                    onClick={handleRequestPermission}
                    className="w-full px-3 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-md hover:bg-primary-700 transition-colors"
                  >
                    Enable Notifications
                  </button>
                </div>
              </div>
            </div>
          )}

          <NotificationDropdown
            onNotificationRead={handleNotificationRead}
            onAllRead={handleAllRead}
            onClose={() => setIsOpen(false)}
            onRefreshCount={fetchUnreadCount}
          />
        </div>
      )}
    </div>
  );
};

// Export both the component and the methods for external updates
export default NotificationBell;
